import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  MessageCircle,
  Menu,
  X,
  Rocket,
  Sparkles,
  Zap,
  Layers,
  Target,
  Wrench,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Reveal, RevealStagger } from "@/components/motion/Reveal";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { TestimonialsSection } from "@/components/Testimonials";
import logoAsset from "@/assets/flashweb-logo.webp.asset.json";
import previewAcquaria from "@/assets/preview-acquaria.jpg";
import previewNorda from "@/assets/preview-norda.jpg";
import previewVoltage from "@/assets/preview-voltage.jpg";
import previewAuroraJet from "@/assets/preview-aurora-jet.jpg";
import previewDiamant from "@/assets/preview-diamant.jpg";
import previewVertex from "@/assets/preview-vertex-snow.jpg.asset.json";

const WA_URL =
  "https://wa.me/41782369200?text=Ciao%20Filippo%2C%0A%0AHo%20visto%20il%20portfolio%20e%20vorrei%20un%20progetto%20simile.%0A%0ANome%20attivit%C3%A0%3A%0ASettore%3A%0ATelefono%3A%0A%0AAttendo%20il%20tuo%20contatto.";

type Filter = "all" | "premium" | "landing";

type CaseStudy = {
  id: string;
  name: string;
  industry: string;
  tier: "Premium" | "Landing";
  image: string;
  accent: string;
  href: string;
  obiettivo: string;
  soluzione: string;
  risultato: string;
  risultatoReal?: boolean;
};

const CASES: CaseStudy[] = [
  {
    id: "acquaria",
    name: "Acquaria Locarno",
    industry: "Acquari di lusso",
    tier: "Premium",
    image: previewAcquaria,
    accent: "#22d3ee",
    href: "/demo/acquaria-locarno",
    obiettivo:
      "Comunicare artigianalità e prezzo su misura senza sembrare un e-commerce. L'atelier voleva raccontare 20 anni di installazioni in ville private senza svendere l'esclusività.",
    soluzione:
      "Sito one-page cinematico con hero video, sezione servizi a 6 blocchi (acquari marini, dolci, biotopi, manutenzione), showcase progetti con metriche di litraggio e form di richiesta preventivo qualificato.",
    risultato:
      "[dato cliente da inserire — es. +X richieste preventivo qualificate/mese, tempo medio sul sito]",
  },
  {
    id: "norda",
    name: "NORDA Living",
    industry: "Arredo casa di design",
    tier: "Premium",
    image: previewNorda,
    accent: "#1e325a",
    href: "/demo/norda-living",
    obiettivo:
      "Posizionare un atelier di mobili su misura tra Lugano e Como su un livello estetico da rivista di design, distinguendosi dai grandi brand di arredamento industriale.",
    soluzione:
      "Palette scura editoriale, tipografia display su misura, showcase collezioni con dettaglio materiali, sezione atelier con foto del processo e configuratore di richiesta per pezzo unico.",
    risultato:
      "[dato cliente da inserire — es. crescita richieste su misura, provenienza geografica clienti]",
  },
  {
    id: "voltage",
    name: "VOLTAGE Lugano",
    industry: "Evento techno",
    tier: "Premium",
    image: previewVoltage,
    accent: "#ef4444",
    href: "/demo/voltage-lugano",
    obiettivo:
      "Vendere biglietti a un pubblico esigente per un evento underground una-notte con tre stage, competendo con festival internazionali.",
    soluzione:
      "Sito ad alto impatto con countdown live, lineup animato, sezione stage con audio preview, ticketing integrato e ottimizzazione mobile-first per il traffico da Instagram.",
    risultato:
      "[dato cliente da inserire — es. % biglietti venduti pre-evento, click-through da IG]",
  },
  {
    id: "skyelite",
    name: "SkyElite",
    industry: "Aviazione privata",
    tier: "Premium",
    image: previewAuroraJet,
    accent: "#202A36",
    href: "/demo/aurora-jet",
    obiettivo:
      "Generare lead qualificati per voli charter da Lugano, Milano e Zurigo, con la promessa di decollo in 4 ore — un'offerta che richiede fiducia immediata.",
    soluzione:
      "Design aeronautico con hero cinematografico, calcolatore di tratta istantaneo, sezione flotta con specifiche tecniche, testimonial di clienti business e form di richiesta con SLA di risposta.",
    risultato:
      "[dato cliente da inserire — es. tempo medio primo contatto, tasso di conversione richiesta → volo]",
  },
  {
    id: "maison-diamant",
    name: "Maison Diamant",
    industry: "Alta gioielleria",
    tier: "Premium",
    image: previewDiamant,
    accent: "#c9a84c",
    href: "/demo/maison-belle",
    obiettivo:
      "Portare online un maestro orafo con 30 anni di storia senza banalizzare il prodotto in una scheda prodotto standard.",
    soluzione:
      "Fotografia macro dei pezzi unici, sezione atelier con racconto del processo di creazione, prenotazione appuntamento privato in showroom e sezione stampa con premi ricevuti.",
    risultato:
      "[dato cliente da inserire — es. numero appuntamenti in showroom prenotati/mese]",
  },
  {
    id: "vertex",
    name: "VERTEX Snow",
    industry: "Snowboard artigianali",
    tier: "Premium",
    image: previewVertex.url,
    accent: "#38bdf8",
    href: "/demo/vertex-snow",
    obiettivo:
      "Vendere snowboard scolpiti a mano ad Andermatt a un pubblico internazionale di rider premium, giustificando un prezzo 3× la media.",
    soluzione:
      "Sito multipagina con configuratore board (forma, camber, grafica), pagina engineering con dettagli tecnici, video del processo in officina e checkout con lead time trasparente.",
    risultato:
      "[dato cliente da inserire — es. % ordini export vs Svizzera, valore medio ordine]",
  },
  // Real client slots — placeholder tier Landing, contenuti da confermare con Filippo
  {
    id: "autoclassica",
    name: "Autoclassica",
    industry: "Auto d'epoca",
    tier: "Landing",
    image: previewAuroraJet,
    accent: "#8b0000",
    href: "/#showcase",
    obiettivo:
      "[cliente reale — contenuti da confermare con Filippo] Presentare la collezione di auto storiche e generare richieste di consulenza per acquisto/vendita.",
    soluzione:
      "[da confermare] Sito vetrina con galleria delle auto, sezione servizi (perizia, restauro, intermediazione), form di contatto qualificato.",
    risultato: "[dato cliente da inserire — non ancora pubblicato]",
  },
  {
    id: "3s-sicurezza",
    name: "3S Sicurezza",
    industry: "Servizi di sicurezza",
    tier: "Landing",
    image: previewNorda,
    accent: "#0f172a",
    href: "/#showcase",
    obiettivo:
      "[cliente reale — contenuti da confermare con Filippo] Comunicare affidabilità e certificazioni per servizi di sicurezza B2B in Ticino.",
    soluzione:
      "[da confermare] Struttura chiara servizio/settore, sezione certificazioni, case study clienti anonimizzati, richiesta sopralluogo.",
    risultato: "[dato cliente da inserire — non ancora pubblicato]",
  },
  {
    id: "pepe-color",
    name: "PEPE COLOR",
    industry: "Verniciature industriali",
    tier: "Landing",
    image: previewVoltage,
    accent: "#f97316",
    href: "/#showcase",
    obiettivo:
      "[cliente reale — contenuti da confermare con Filippo] Portare online uno storico laboratorio di verniciature con richieste preventivo online.",
    soluzione:
      "[da confermare] Sito con showcase lavorazioni, sezione tecnologie di verniciatura, form preventivo con upload disegno tecnico.",
    risultato: "[dato cliente da inserire — non ancora pubblicato]",
  },
];

const SECTOR_STATS = [
  { label: "Ristorazione & Hospitality", pct: 26 },
  { label: "Retail & E-commerce", pct: 22 },
  { label: "Servizi professionali", pct: 18 },
  { label: "Eventi & Cultura", pct: 12 },
  { label: "Industriale & B2B", pct: 12 },
  { label: "Altro", pct: 10 },
];

export const Route = createFileRoute("/portfolio")({
  head: () => ({
    meta: [
      { title: "Portfolio — 60+ siti consegnati | FlashWeb Agency" },
      {
        name: "description",
        content:
          "Case study reali: obiettivo del cliente, soluzione costruita, risultato ottenuto. Dal locale di lusso alla PMI industriale.",
      },
      { property: "og:title", content: "Portfolio — FlashWeb Agency" },
      {
        property: "og:description",
        content:
          "60+ siti consegnati con precisione svizzera. Vedi come lavoriamo: obiettivo, soluzione e risultato per ogni progetto.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://www.flash-web.ch/portfolio" },
    ],
    links: [{ rel: "canonical", href: "https://www.flash-web.ch/portfolio" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://www.flash-web.ch/" },
            { "@type": "ListItem", position: 2, name: "Portfolio", item: "https://www.flash-web.ch/portfolio" },
          ],
        }),
      },
    ],
  }),
  component: PortfolioPage,
});

// ─── Navbar / Footer (same pattern) ───
function Navbar() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-border/50 bg-background/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
        <Link to="/" className="flex min-w-0 items-center gap-2 group">
          <img
            src={logoAsset.url}
            alt="FlashWeb Agency"
            width="144"
            height="36"
            className="h-9 w-auto brightness-110 contrast-125 transition-transform group-hover:scale-105"
          />
          <span className="font-display text-lg font-bold tracking-tight">FlashWeb</span>
        </Link>
        <div className="flex items-center gap-2">
          <Button variant="hero" size="default" asChild className="hidden sm:inline-flex fw-shimmer">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              Contattaci <ArrowRight />
            </a>
          </Button>
          <Button variant="hero" size="sm" asChild className="sm:hidden fw-shimmer">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              Contattaci <ArrowRight className="h-4 w-4" />
            </a>
          </Button>
          <button
            type="button"
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border/60 text-foreground"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-border/50 bg-background/95 backdrop-blur-xl">
          <div className="mx-auto flex max-w-6xl flex-col px-4 py-3 text-sm">
            {[
              { href: "/", label: "Home" },
              { href: "/servizi", label: "Servizi" },
              { href: "/prezzi", label: "Prezzi" },
              { href: "/come-funziona", label: "Come funziona" },
              { href: "/portfolio", label: "Portfolio" },
              { href: "/recensioni", label: "Recensioni" },
              { href: "/trasparenza", label: "Trasparenza" },
              { href: "/chi-siamo", label: "Chi siamo" },
              { href: "/faq", label: "FAQ" },
              { href: "/blog", label: "Blog" },
              { href: "/contatti", label: "Contatti" },
              { href: "/portal", label: "Area clienti" },
            ].map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-3 text-foreground/90 transition-colors hover:bg-muted hover:text-foreground"
              >
                {l.label}
              </a>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-border bg-card/30">
      <div className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <img src={logoAsset.url} alt="FlashWeb Agency" width="112" height="28" className="h-7 w-auto" />
              <span className="font-display text-sm font-bold">FlashWeb Agency</span>
            </div>
            <p className="text-sm text-muted-foreground">© {new Date().getFullYear()} — Tutti i diritti riservati.</p>
            <p className="text-sm text-muted-foreground">
              Un brand di <span className="font-semibold text-foreground">FVT FuturaVision Technology GmbH</span>
            </p>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Sedi</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><span className="font-semibold text-foreground">Bareerstrasse 75</span><br />6300 Zug, Svizzera</p>
              <p><span className="font-semibold text-foreground">Via Ai Prati 2</span><br />Bedano, Svizzera</p>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Contatti</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p><a href="mailto:info@flash-web.ch" className="font-semibold text-foreground hover:underline">info@flash-web.ch</a></p>
              <p><a href="https://wa.me/41782369200" target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground hover:underline">WhatsApp +41 78 236 92 00</a></p>
            </div>
          </div>
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Informazioni legali</h4>
            <p className="text-sm text-muted-foreground">N° registro commercio:<br /><span className="font-semibold text-foreground">CH-170.4.022.832-9</span></p>
            <div className="flex flex-col gap-1.5 pt-1 text-sm">
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">Privacy Policy</Link>
              <Link to="/cookie-policy" className="text-muted-foreground hover:text-foreground">Cookie Policy</Link>
              <Link to="/termini-condizioni" className="text-muted-foreground hover:text-foreground">Termini e Condizioni</Link>
              <Link to="/impressum" className="text-muted-foreground hover:text-foreground">Impressum</Link>
            </div>
          </div>
        </div>
        <nav aria-label="Naviga" className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t border-border/60 pt-6 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <Link to="/servizi" className="hover:text-foreground">Servizi</Link>
          <Link to="/prezzi" className="hover:text-foreground">Prezzi</Link>
          <Link to="/come-funziona" className="hover:text-foreground">Come funziona</Link>
          <Link to="/portfolio" className="hover:text-foreground">Portfolio</Link>
          <Link to="/recensioni" className="hover:text-foreground">Recensioni</Link>
          <Link to="/trasparenza" className="hover:text-foreground">Trasparenza</Link>
          <Link to="/chi-siamo" className="hover:text-foreground">Chi siamo</Link>
          <Link to="/faq" className="hover:text-foreground">FAQ</Link>
          <Link to="/blog" className="hover:text-foreground">Blog</Link>
          <Link to="/contatti" className="hover:text-foreground">Contatti</Link>
        </nav>
        <div className="mt-6 flex flex-col gap-2 pt-4 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} FVT FuturaVision Technology GmbH — Tutti i diritti riservati.</p>
          <p>Realizzato con precisione svizzera 🇨🇭</p>
        </div>
      </div>
    </footer>
  );
}

// ─── Case study card ───
function CaseCard({ c }: { c: CaseStudy }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="group relative flex h-full w-full flex-col overflow-hidden rounded-3xl border border-border bg-card/60 text-left backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:border-primary/50 hover:shadow-[0_30px_80px_-20px_var(--primary)]"
        >
          <div className="relative aspect-[16/10] overflow-hidden">
            <div
              className="absolute inset-0 opacity-30 blur-2xl transition-opacity group-hover:opacity-60"
              style={{ background: `radial-gradient(circle at 50% 50%, ${c.accent}, transparent 60%)` }}
            />
            <img
              src={c.image}
              alt={`Anteprima ${c.name}`}
              loading="lazy"
              className="relative h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <span
              className="absolute left-4 top-4 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest backdrop-blur"
              style={{ borderColor: `${c.accent}55`, color: c.accent, background: `${c.accent}15` }}
            >
              {c.tier}
            </span>
          </div>
          <div className="flex flex-1 flex-col p-6">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              {c.industry}
            </div>
            <h3 className="mt-2 font-display text-xl font-bold tracking-tight">{c.name}</h3>
            <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">{c.obiettivo}</p>
            <div className="mt-auto flex items-center justify-between pt-6">
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                Leggi il case study <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
              <span className="text-xs text-muted-foreground">Click per aprire</span>
            </div>
          </div>
        </button>
      </DialogTrigger>

      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto border-border bg-card/95 backdrop-blur-xl p-0">
        <div className="relative aspect-[16/9] overflow-hidden rounded-t-lg">
          <div
            className="absolute inset-0 opacity-40 blur-2xl"
            style={{ background: `radial-gradient(circle at 30% 40%, ${c.accent}, transparent 60%)` }}
          />
          <img src={c.image} alt={c.name} className="relative h-full w-full object-cover" />
        </div>
        <div className="p-6 sm:p-8">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest"
              style={{ borderColor: `${c.accent}55`, color: c.accent, background: `${c.accent}15` }}
            >
              {c.tier}
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              {c.industry}
            </span>
          </div>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">{c.name}</h2>

          <div className="mt-8 space-y-6">
            {[
              { Icon: Target, label: "Obiettivo", text: c.obiettivo },
              { Icon: Wrench, label: "Soluzione", text: c.soluzione },
              { Icon: TrendingUp, label: "Risultato", text: c.risultato },
            ].map(({ Icon, label, text }) => (
              <div key={label} className="rounded-2xl border border-border bg-background/60 p-5">
                <div className="flex items-center gap-2.5">
                  <span className="grid h-8 w-8 place-items-center rounded-lg bg-primary/15 text-primary">
                    <Icon className="h-4 w-4" />
                  </span>
                  <h3 className="font-display text-sm font-bold uppercase tracking-widest text-primary">{label}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-foreground/90 sm:text-base">{text}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            {c.href.startsWith("/demo/") ? (
              <Button variant="hero" size="lg" asChild className="fw-shimmer">
                <a href={c.href} target="_blank" rel="noopener noreferrer">
                  Vedi la demo live <ArrowUpRight className="h-4 w-4" />
                </a>
              </Button>
            ) : null}
            <Button variant="outlineHero" size="lg" asChild>
              <a href={WA_URL} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="h-4 w-4" /> Voglio qualcosa di simile
              </a>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════ PAGE ═══════════════
function PortfolioPage() {
  const [filter, setFilter] = useState<Filter>("all");

  const filtered = CASES.filter((c) => {
    if (filter === "all") return true;
    if (filter === "premium") return c.tier === "Premium";
    return c.tier === "Landing";
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div className="absolute inset-0 bg-grid [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)]" />
        <div className="absolute inset-0" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto max-w-4xl px-6 py-24 text-center sm:py-32">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
              Portfolio
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl md:text-7xl">
              Non progetti. <span className="text-gradient">Risultati.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground sm:text-xl">
              Ogni sito che vedi qui è nato da un obiettivo di business preciso. Clicca su
              una card per leggere il case study completo: cosa serviva, cosa abbiamo
              costruito, cosa è successo dopo.
            </p>
          </Reveal>

          <RevealStagger className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-4" stagger={0.06}>
            {[
              { n: "60+", l: "Siti online", Icon: Rocket },
              { n: "4.9/5", l: "Valutazione media", Icon: Sparkles },
              { n: "48h", l: "Tempo di consegna", Icon: Zap },
              { n: "14", l: "Settori serviti", Icon: Layers },
            ].map(({ n, l, Icon }) => (
              <div
                key={l}
                className="rounded-2xl border border-border bg-card/60 p-4 backdrop-blur transition-colors hover:border-primary/50"
              >
                <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-lg bg-primary/15 text-primary">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="mt-3 font-display text-2xl font-bold tracking-tight text-gradient sm:text-3xl">{n}</div>
                <div className="mt-1 text-xs text-muted-foreground">{l}</div>
              </div>
            ))}
          </RevealStagger>
        </div>
      </section>

      {/* GRID CASE STUDY */}
      <section className="relative mx-auto w-full max-w-6xl px-6 py-20 sm:py-24">
        <Reveal>
          <div className="mb-10 flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
                Case study
              </div>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                Progetti reali, <span className="text-gradient">raccontati onestamente.</span>
              </h2>
            </div>
            <div className="inline-flex gap-1 rounded-2xl border border-border bg-card/40 p-1.5 backdrop-blur">
              {([
                { k: "all", l: "Tutti" },
                { k: "premium", l: "Premium" },
                { k: "landing", l: "Landing" },
              ] as { k: Filter; l: string }[]).map((t) => (
                <button
                  key={t.k}
                  onClick={() => setFilter(t.k)}
                  className={`rounded-xl px-4 py-2 text-xs font-semibold transition-all ${
                    filter === t.k
                      ? "bg-gradient-to-r from-primary to-fuchsia-500 text-primary-foreground shadow-[0_10px_30px_-10px_var(--primary)]"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t.l}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <RevealStagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" stagger={0.06}>
          {filtered.map((c) => (
            <CaseCard key={c.id} c={c} />
          ))}
        </RevealStagger>
      </section>

      {/* SETTORI IN NUMERI */}
      <section className="relative border-y border-border bg-card/40">
        <div className="pointer-events-none absolute inset-0 opacity-50" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto w-full max-w-5xl px-6 py-20 sm:py-28">
          <Reveal>
            <div className="mb-12 max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur">
                <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_10px_var(--primary)]" />
                Settori in numeri
              </div>
              <h2 className="mt-4 font-display text-4xl font-bold tracking-tight sm:text-5xl">
                Chi ci ha <span className="text-gradient">scelto finora.</span>
              </h2>
              <p className="mt-4 text-muted-foreground">
                Distribuzione dei 60+ siti consegnati per settore.{" "}
                <span className="text-foreground/70">
                  [percentuali indicative da confermare con Filippo prima di pubblicare]
                </span>
              </p>
            </div>
          </Reveal>

          <div className="space-y-4">
            {SECTOR_STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.05}>
                <div>
                  <div className="mb-2 flex items-baseline justify-between gap-3">
                    <span className="font-display text-sm font-semibold sm:text-base">{s.label}</span>
                    <span className="font-display text-lg font-bold text-gradient sm:text-xl">{s.pct}%</span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-background/70">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-primary to-fuchsia-500 shadow-[0_0_20px_-4px_var(--primary)] transition-all duration-1000"
                      style={{ width: `${s.pct}%` }}
                    />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RECENSIONI */}
      <section className="relative">
        <TestimonialsSection />
      </section>

      {/* CTA */}
      <section className="relative border-t border-border bg-card/40">
        <div className="pointer-events-none absolute inset-0" style={{ background: "var(--gradient-hero)" }} />
        <div className="relative mx-auto max-w-4xl px-6 py-20 text-center sm:py-28">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-medium text-primary backdrop-blur">
              <Zap className="h-3 w-3" />
              Il tuo progetto è il prossimo
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-display text-4xl font-bold tracking-tight sm:text-5xl">
              Ti serve qualcosa di <span className="text-gradient">simile?</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              Raccontaci l'obiettivo. Ti rispondiamo con un piano concreto in giornata —
              oppure guarda prima cosa include ogni piano.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button variant="hero" size="xl" asChild className="fw-shimmer">
                <a href={WA_URL} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="h-5 w-5" /> Parla con Filippo
                </a>
              </Button>
              <Button variant="outlineHero" size="xl" asChild>
                <Link to="/servizi">
                  Vedi i piani <ArrowRight />
                </Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
